import { first, isEmpty } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { SaleInvoiceAddress } from 'src/components/SaleInvoice';
import {
  Content,
  getContent,
  IContentContext,
  useContentContext,
} from 'src/contexts/ContentContext';
import { Checkbox } from 'src/core/interim/Checkbox';
import { DatePickerInput } from 'src/core/POS/DateSelector';
import { PosFormField } from 'src/core/POS/PosFormField';
import { getTextFieldState, PosTextField } from 'src/core/POS/PosTextField';
import { Button, Stack, TooltipPopover } from 'src/core/ui';
import { ContentId } from 'src/utils/constants/contentId';
import {
  MARKETPLACE_PAYMENT_LINE_TYPE_TO_CID,
  MARKETPLACE_PAYMENT_TERM_TO_CID,
} from 'src/utils/constants/contentIdMaps';
import { getLocaleFromLanguageOrCurrent } from 'src/utils/localeUtils';
import { Marketplace, SaleDetails } from 'src/WebApiController';

import { SaleInvoiceAddressInput } from './SaleInvoiceOptionsPopover.components';
import * as styles from './SaleInvoiceOptionsPopover.css';

const isAddressReady = (address: SaleInvoiceAddress) => {
  return Object.values(address).every((v) => !isEmpty(v));
};

const getDefaultInput = (
  sale: SaleDetails,
  contentContext: IContentContext
): SaleInvoiceFormData => {
  const termsContentId = sale.mkpPmtTerm
    ? MARKETPLACE_PAYMENT_TERM_TO_CID[sale.mkpPmtTerm]
    : null;
  const termsText = termsContentId
    ? getContent(termsContentId, contentContext)
    : undefined;
  const paymentType = first((sale.mkpLines ?? []).map((l) => l.type));
  const paymentTypeContentId = paymentType
    ? MARKETPLACE_PAYMENT_LINE_TYPE_TO_CID[paymentType]
    : null;
  const paymentTypeText = paymentTypeContentId
    ? getContent(paymentTypeContentId, contentContext)
    : undefined;
  return {
    dueDate: sale.saleDate,
    paymentTerms: termsText,
    paymentType: paymentTypeText,
    shipping: DefaultAddress,
    billing: DefaultAddress,
    sameAddress: false,
  };
};

const DefaultAddress = {
  name: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  country: '',
  phoneNumber: '',
};

export type SaleInvoiceFormData = {
  dueDate: string;
  paymentTerms?: string;
  paymentType?: string;
  billing: SaleInvoiceAddress;
  shipping: SaleInvoiceAddress;
  sameAddress: boolean;
};

interface SaleInvoiceOptionsPopoverProps {
  sale: SaleDetails;
  children: React.ReactNode;
  onConfirm: (data: SaleInvoiceFormData) => void;
  disabled?: boolean;
}

export const SaleInvoiceOptionsPopover = ({
  sale,
  children,
  onConfirm,
  disabled,
}: SaleInvoiceOptionsPopoverProps) => {
  const contentContext = useContentContext();

  const {
    watch,
    getValues,
    register,
    control,
    handleSubmit,
    getFieldState,
    setValue,
  } = useForm<SaleInvoiceFormData>({
    defaultValues: getDefaultInput(sale, contentContext),
  });

  const isOfflineSale = useMemo(
    () => sale.mkp === Marketplace.Offline,
    [sale.mkp]
  );

  const data = watch();

  const onSumbit = useCallback(() => {
    handleSubmit(onConfirm)();
  }, [handleSubmit, onConfirm]);

  const readyToSubmit = useMemo(() => {
    return (
      data.dueDate &&
      isAddressReady(data.shipping) &&
      (data.sameAddress || isAddressReady(data.billing))
    );
  }, [data.billing, data.dueDate, data.sameAddress, data.shipping]);

  const onToggleSameAsShippingAddress = useCallback(() => {
    const current = getValues();
    if (!current.sameAddress) {
      setValue('billing', current.shipping);
    }
    setValue('sameAddress', !current.sameAddress);
  }, [getValues, setValue]);

  return (
    <TooltipPopover disabled={disabled} showArrow triggerContent={children}>
      <Stack direction="column" gap="xl" className={styles.container}>
        <Stack direction="row" gap="m">
          <Controller
            control={control}
            name={`dueDate`}
            rules={{ required: true }}
            render={({ field: { ...field }, fieldState }) => (
              <PosFormField
                label={
                  <div className={styles.inputLabel}>
                    <Content id={ContentId.DueDate} />
                  </div>
                }
              >
                <DatePickerInput
                  fieldError={getTextFieldState(fieldState?.error)}
                  locale={getLocaleFromLanguageOrCurrent()}
                  {...field}
                  onDateChange={(d) => {
                    setValue(field.name, d.toISOString());
                  }}
                />
              </PosFormField>
            )}
          />
          <PosFormField
            label={
              <div className={styles.inputLabel}>
                <Content id={ContentId.MarketplaceTerms} />
              </div>
            }
          >
            <PosTextField
              disabled={!isOfflineSale}
              rootProps={{
                state: getTextFieldState(getFieldState(`paymentTerms`)?.error),
              }}
              {...register(`paymentTerms`)}
            />
          </PosFormField>
          <PosFormField
            label={
              <div className={styles.inputLabel}>
                <Content id={ContentId.PaymentType} />
              </div>
            }
          >
            <PosTextField
              disabled={!isOfflineSale}
              rootProps={{
                state: getTextFieldState(getFieldState(`paymentType`)?.error),
              }}
              {...register(`paymentType`)}
            />
          </PosFormField>
        </Stack>

        <Stack direction="row" gap="l">
          <Stack direction="column" gap="m" style={{ flex: 1 }}>
            <div className={styles.sectionHeader}>
              <Content id={ContentId.ShippingAddress} />
            </div>
            <SaleInvoiceAddressInput
              type="shipping"
              register={register}
              getFieldState={getFieldState}
            />
          </Stack>

          <Stack direction="column" gap="m" style={{ flex: 1 }}>
            <Stack justifyContent="spaceBetween" alignItems="center">
              <div className={styles.sectionHeader}>
                <Content id={ContentId.BillingAddress} />
              </div>
              <Checkbox
                checked={data.sameAddress}
                onChange={onToggleSameAsShippingAddress}
                label={<Content id={ContentId.SameAsShipping} />}
              />
            </Stack>

            {!data.sameAddress && (
              <SaleInvoiceAddressInput
                type="billing"
                register={register}
                getFieldState={getFieldState}
              />
            )}
          </Stack>
        </Stack>

        <Stack direction="row" gap="m" justifyContent="end">
          <Button disabled={!readyToSubmit} onClick={onSumbit}>
            <Content id={ContentId.PrintInvoice} />
          </Button>
        </Stack>
      </Stack>
    </TooltipPopover>
  );
};
